import { Component, DestroyRef, effect, inject, OnInit, PLATFORM_ID, Signal } from '@angular/core';
import { FormControl, FormGroup, NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { IconSearchComponent } from '../../shared/components/icons/icon-search/icon-search.component';
import { HairstylesService } from '../../shared/services/hairstyles/hairstyles.service';
import { IHairstyleFilters, IPageHairstyleResponse } from '../../shared/interfaces/hairstyle.interface';
import { NgOptimizedImage, isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgxPaginationModule } from 'ngx-pagination';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MetasService } from '../../core/services/meta-service/metas.service';
import { getSEOMetaData } from '../../core/constants/seo.constants';
import { toSlug } from 'src/app/shared/utils/slug.utils';
import { HAIRSTYLE_PLACEHOLDER } from '../../shared/constants/app.constants';
import { PricePipe } from '../../shared/pipes/price.pipe';
import { formatDuration } from '../../shared/utils/product.utils';

interface SearchHairstyleForm {
  hairstyle: FormControl<string>;
  maxPrice: FormControl<number | null>;
}

@Component({
  selector: 'braidsbook-hairstyles',
  imports: [
    ReactiveFormsModule,
    IconSearchComponent,
    NgOptimizedImage,
    NgxPaginationModule,
    RouterLink,
    TranslatePipe,
    PricePipe
  ],
  templateUrl: './hairstyles.component.html',
  styles: ``
})
export class HairstylesComponent implements OnInit {
  #hairstylesService: HairstylesService = inject(HairstylesService);
  #destroyRef: DestroyRef = inject(DestroyRef);
  #platformId = inject(PLATFORM_ID);
  #metasService: MetasService = inject(MetasService);
  #translateService: TranslateService = inject(TranslateService);

  hairstylesPaginated: Signal<IPageHairstyleResponse | null> = this.#hairstylesService.hairstylesPaginated;
  isLoadingHairstyles: Signal<boolean> = this.#hairstylesService.isLoading;
  protected readonly toSlug = toSlug;

  searchHairstyleForm: FormGroup<SearchHairstyleForm> = inject(NonNullableFormBuilder).group<SearchHairstyleForm>({
    hairstyle: new FormControl('', { nonNullable: true }),
    maxPrice: new FormControl(null, { nonNullable: false })
  });

  constructor() {
    effect((): void => {
      this.#hairstylesService.getPaginatedHairstyles()
        .pipe(takeUntilDestroyed(this.#destroyRef)).subscribe();
    });
  }

  async ngOnInit(): Promise<void> {
    // Update SEO meta tags
    const metaData = getSEOMetaData('hairstyles');
    if (metaData) {
      await this.#metasService.updateMetaTags(metaData);
    }

    // Update language attribute
    const currentLang = this.#translateService.getCurrentLang() || 'en';
    this.#metasService.updateLanguage(currentLang);

    // Add Breadcrumbs
    this.#metasService.updateStructuredData({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      'itemListElement': [
        {
          '@type': 'ListItem',
          'position': 1,
          'name': 'Home',
          'item': 'https://braidsbook.com/'
        },
        {
          '@type': 'ListItem',
          'position': 2,
          'name': 'Hairstyles',
          'item': 'https://braidsbook.com/hairstyles'
        }
      ]
    });
  }

  onPageChange(event: number): void {
    this.#hairstylesService.page.set(event);
    if (isPlatformBrowser(this.#platformId)) {
      window.scrollTo(0, 0);
    }
  }

  onClearForm(): void {
    this.searchHairstyleForm.reset();
    this.#hairstylesService.filters.set(null);
  }

  onSubmitForm(): void {
    const { hairstyle, maxPrice } = this.searchHairstyleForm.getRawValue();
    const filter: IHairstyleFilters = {
      nom: hairstyle.toLowerCase(),
      prix_moy: maxPrice?.toString()
    };
    if (this.searchHairstyleForm.touched) {
      this.#hairstylesService.page.set(1);
      this.#hairstylesService.filters.set(filter);
    }

  }

  protected readonly HAIRSTYLE_PLACEHOLDER = HAIRSTYLE_PLACEHOLDER;
  protected readonly formatDuration = formatDuration;
}
